import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';
import { useRHFNestedFormContext } from '@openx/components-core/lib/Form/context/RHFNestedFormContext.tsx';
import { InfoTile } from '@openx/components-core/lib/InfoTile/InfoTile.tsx';
import { ACL_TYPE } from '@openx/types/aclType.ts';
import { dealFormField, type GroupDeal, rboAlertKind, rboConfigField, rboConfigMode } from '@openx/types/deal.ts';
import { PackageFields, type PackageFormState } from '@openx/types/package.ts';
import { TargetingFields } from '@openx/types/targeting/targeting.ts';
import type { RequiredTargetingFormType } from '@openx/types/targeting/targetingPresets.ts';
import { cleanUpNulls } from '@openx/utils/lib/cleanUpNulls.ts';
import { usePermissionContext } from '@openx/utils/permission/context/PermissionContext.tsx';
import { isAllowedByCompiledAcl } from '@openx/utils/permission/validators.ts';
import { useMemo } from 'react';
import { useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { isTargetingRboCompatible } from '../../../../../utils/isTargetingRboCompatible.ts';
import { useRboDisplayedAlert } from '../RboDisplayedAlertContext.tsx';

const hasMetaGroupDealsWithRboEnabled = (groupDealMeta: GroupDeal[] | undefined, dealUid: string | undefined) => {
  if (!groupDealMeta) {
    return false;
  }

  return groupDealMeta.some(deal => deal.rboConfig && deal.uid !== dealUid);
};

type ApplyRboAlertProps = {
  targetingParams?: RequiredTargetingFormType | null;
};

export const ApplyRboAlert = ({ targetingParams }: ApplyRboAlertProps) => {
  const { t } = useTranslation();
  const rboDisplayed = useRboDisplayedAlert();
  const { parentForm, isNested } = useRHFNestedFormContext();
  const { useIsAllowed } = usePermissionContext();
  const { isAllowed } = useIsAllowed();
  const observedFormat = useWatch<PackageFormState>({
    name: `${PackageFields.TARGETING}.${TargetingFields.INVENTORY_FORMAT}.ad_placement`,
  });
  const observedTvDevices = useWatch<PackageFormState>({
    name: `${PackageFields.TARGETING}.${TargetingFields.INVENTORY_FORMAT}.device_type.tv_devices`,
  });

  const resultsEnabled = useMemo(() => isAllowed(isAllowedByCompiledAcl(ACL_TYPE.RESULTS_ENABLED)), [isAllowed]);

  if (rboDisplayed?.displayedKind === rboAlertKind.GROUP_VALIDATION) {
    return null;
  }

  if (!isNested || !resultsEnabled) {
    return null;
  }

  const format = observedFormat || targetingParams?.ad_placement;
  const hasTvDevices = !!(observedTvDevices ?? targetingParams?.device_type?.tv_devices);
  const rboConfig = parentForm?.getValues(dealFormField.RBO_CONFIG);

  const groupDealMeta = parentForm?.getValues(dealFormField.METADATA_GROUP_DEAL);
  const dealUid = parentForm?.getValues(dealFormField.UID);

  if (
    !format?.val ||
    !isTargetingRboCompatible(format.val, hasTvDevices) ||
    hasMetaGroupDealsWithRboEnabled(groupDealMeta, dealUid)
  ) {
    return null;
  }

  const handleApplyRbo = () => {
    parentForm?.setValue(
      `${dealFormField.RBO_CONFIG}`,
      { [rboConfigField.MODE]: rboConfigMode.BOOST },
      { shouldDirty: true, shouldValidate: false }
    );

    setTimeout(() => {
      document.getElementById('results-by-openx-section')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
  };

  const hasRboValues = !!cleanUpNulls(rboConfig);
  if (hasRboValues) {
    return (
      <InfoTile
        data-test="rbo-enabled-alert"
        closeable
        severity="info"
        icon={<AutoAwesomeOutlinedIcon />}
        item={{
          title: t('Engagement Optimization'),
          body: (
            <>
              {t('You have selected ')}
              <strong>{t('Video')}</strong>
              {t(' or ')}
              <strong>{t('Banner')}</strong>
              {t(' package targeting. ')}
              {t(
                'As Engagement optimization is enabled above, the corresponding model has been applied automatically.'
              )}
            </>
          ),
        }}
      />
    );
  }

  return (
    <InfoTile
      data-test="apply-rbo-alert"
      closeable
      severity="info"
      icon={<AutoAwesomeOutlinedIcon />}
      item={{
        title: t('Your deal qualifies for Engagement optimization'),
        body: t('Dynamically optimize your deal with AI to include inventory most likely to drive ad engagement.'),
      }}
      secondaryAction={{ onAction: handleApplyRbo, button: t('Apply Engagement Optimization') }}
    />
  );
};
